/**
 * Everything the app needs in scope, assembled in one place.
 *
 * A 401 is handled here rather than in each screen. Any query can be the one
 * that discovers the session ended, and a rule written per screen is a rule
 * that will be missing from the screen added next.
 *
 * What it does about it is deliberately small: it forgets that anyone is
 * signed in. The router then renders the sign-in screen because that is what
 * it renders when there is no session — no redirect, so no loop between a
 * guarded page and the page guarding it.
 */
import { QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState } from "react";

import { SESSION_KEY } from "@/features/auth/useSession";
import { installViewAsHeaders } from "@/shared/session/viewAs";
import { client } from "@/shared/api/client.gen";
import { isUnauthorized } from "@/shared/http/errors";

export function makeQueryClient(onUnauthorized: () => void): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error) => {
        if (isUnauthorized(error)) onUnauthorized();
      },
    }),
    defaultOptions: {
      queries: {
        // A background tab stops every refetch timer, so a returning owner
        // saw «данные устарели» while the server was current (2026-10-26).
        // Coming back re-reads what is older than staleTime; the API serves
        // from the database and never calls the exchange on a read.
        refetchOnWindowFocus: true,
        // A refused request is refused. Retrying a 401 only delays the moment
        // the owner is told to sign in again.
        retry: (failureCount, error) => !isUnauthorized(error) && failureCount < 2,
        staleTime: 10_000,
      },
      mutations: {
        onError: (error) => {
          if (isUnauthorized(error)) onUnauthorized();
        },
      },
    },
  });
}

export function Providers({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element {
  // Built once per mount, and the handler closes over the client it belongs
  // to. Held here rather than in a module variable: two mounts in one test run
  // would otherwise share one, and the second would sign the first one out.
  const [queryClient] = useState(() => {
    // The cookie is HttpOnly, so the browser attaches it and nothing here
    // reads it. `credentials: "include"` is what makes it be sent at all.
    client.setConfig({ credentials: "include" });
    installViewAsHeaders();

    let created: QueryClient | null = null;
    created = makeQueryClient(() => {
      created?.setQueryData(SESSION_KEY, null);
    });
    return created;
  });

  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>;
}
